import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

// Cache rendered variant HTML for the lifetime of the page. This makes switching
// back to a recently viewed variant effectively instant without changing Shopify's
// price, inventory, media, or add-to-cart update flow.
const VARIANT_RESPONSE_CACHE = new Map();
const VARIANT_RESPONSE_CACHE_LIMIT = 24;

// -----------------------------------------------------------------------------
// Aururio color preload
// Preloads the exact Shopify response URL used by a first-time color click.
// Background bulk prefetch waits until the page is loaded and idle, while user
// intent (hover / pointerdown / focus) still prefetches the target immediately.
// -----------------------------------------------------------------------------
const VARIANT_PREFETCH_INFLIGHT = new Map();
const VARIANT_PREFETCH_LABELS = new Map();
const VARIANT_PREFETCH_DESKTOP_CONCURRENCY = 2;
const VARIANT_PREFETCH_MOBILE_CONCURRENCY = 1;

function getAururioOptionLabel(option) {
  return (
    option?.value ||
    option?.getAttribute?.('aria-label') ||
    option?.getAttribute?.('title') ||
    option?.dataset?.optionValueId ||
    'Unknown'
  );
}

function prefetchVariantResponse(url, label = 'Unknown') {
  if (!url) return Promise.resolve(null);

  VARIANT_PREFETCH_LABELS.set(url, label);

  const cached = VARIANT_RESPONSE_CACHE.get(url);
  if (cached) return Promise.resolve(cached);

  const inflight = VARIANT_PREFETCH_INFLIGHT.get(url);
  if (inflight) return inflight;

  const startedAt = performance.now();

  const request = fetch(url, {
    credentials: 'same-origin',
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Variant prefetch failed with status ${response.status}`);
      }
      return response.text();
    })
    .then((html) => {
      cacheVariantResponse(url, html);
      return html;
    })
    .catch((error) => {
      console.warn(`[Aururio Prefetch] FAILED: ${label}`, error);
      throw error;
    })
    .finally(() => {
      VARIANT_PREFETCH_INFLIGHT.delete(url);
      void startedAt;
    });

  VARIANT_PREFETCH_INFLIGHT.set(url, request);
  return request;
}

function cacheVariantResponse(url, html) {
  if (VARIANT_RESPONSE_CACHE.has(url)) {
    VARIANT_RESPONSE_CACHE.delete(url);
  }

  VARIANT_RESPONSE_CACHE.set(url, html);

  if (VARIANT_RESPONSE_CACHE.size > VARIANT_RESPONSE_CACHE_LIMIT) {
    const oldestKey = VARIANT_RESPONSE_CACHE.keys().next().value;
    if (oldestKey) VARIANT_RESPONSE_CACHE.delete(oldestKey);
  }
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  #aururioColorPrefetchStarted = false;

  #aururioColorIntentHandler = (event) => {
    if (this.dataset.templateProductMatch !== 'true') return;
    if (!(event.target instanceof Element)) return;

    const colorLabel = event.target.closest('.variant-option--aururio-color .aururio-color-label');
    if (!colorLabel || !this.contains(colorLabel)) return;

    const option = colorLabel.querySelector('input[data-option-value-id]');
    if (!(option instanceof HTMLInputElement)) return;

    const requestUrl = this.#buildAururioPrefetchUrl(option);
    if (!requestUrl) return;

    prefetchVariantResponse(requestUrl, getAururioOptionLabel(option)).catch(() => {});
  };

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    if (this.dataset.templateProductMatch === 'true') {
      this.addEventListener('pointerover', this.#aururioColorIntentHandler, { passive: true });
      this.addEventListener('pointerdown', this.#aururioColorIntentHandler, { passive: true });
      this.addEventListener('focusin', this.#aururioColorIntentHandler);
      this.#startAururioColorPrefetch();
    }
  }

  #startAururioColorPrefetch() {
    if (this.#aururioColorPrefetchStarted || !this.isConnected) return;

    this.#aururioColorPrefetchStarted = true;

    const schedule = () => {
      if (!this.isConnected) return;

      const run = () => {
        if (!this.isConnected) return;
        void this.#prefetchAururioColors();
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 3500 });
      } else {
        window.setTimeout(run, 1600);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  }

  #buildAururioPrefetchUrl(candidateOption) {
    const connectedProductUrl = candidateOption.dataset.connectedProductUrl;
    let productUrl = connectedProductUrl || this.dataset.productUrl;

    if (!productUrl) return null;

    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) {
      params.push(`view=${viewParamValue}`);
    }

    const candidateFieldset = candidateOption.closest('fieldset');
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    );

    const optionValueIds = selectedOptions
      .map((option) => {
        if (candidateFieldset && option.closest('fieldset') === candidateFieldset) {
          return candidateOption.dataset.optionValueId;
        }

        return option.dataset.optionValueId;
      })
      .filter(Boolean);

    if (optionValueIds.length) {
      params.push(`option_values=${optionValueIds.join(',')}`);
    }

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId));

    if (closestSectionId) {
      if (productUrl.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }

      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    const isCombinedListingChange = Boolean(connectedProductUrl);
    const shopifySection = !isCombinedListingChange ? this.closest('[id^="shopify-section-"]') : null;
    const shopifySectionId = shopifySection?.id?.replace(/^shopify-section-/, '');

    if (shopifySectionId) {
      if (productUrl.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }

      return `${productUrl}?section_id=${encodeURIComponent(shopifySectionId)}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  async #prefetchAururioColors() {
    const colorFieldset = this.querySelector('.variant-option--aururio-color');

    if (!colorFieldset) return;

    const colorOptions = Array.from(
      colorFieldset.querySelectorAll('input[data-option-value-id]')
    ).sort((a, b) => Number(a.checked) - Number(b.checked));

    const jobs = [];
    const seenUrls = new Set();

    colorOptions.forEach((option) => {
      if (option.checked) return;

      const requestUrl = this.#buildAururioPrefetchUrl(option);
      if (!requestUrl || seenUrls.has(requestUrl)) return;

      seenUrls.add(requestUrl);

      const label = getAururioOptionLabel(option);
      VARIANT_PREFETCH_LABELS.set(requestUrl, label);
      jobs.push({ requestUrl, label });
    });

    if (!jobs.length) return;

    let index = 0;
    let completed = 0;
    let failed = 0;

    const worker = async () => {
      while (index < jobs.length) {
        const job = jobs[index++];

        try {
          await prefetchVariantResponse(job.requestUrl, job.label);
          completed += 1;
        } catch (_error) {
          failed += 1;
        }
      }
    };

    const maxConcurrency = window.matchMedia('(max-width: 749px)').matches
      ? VARIANT_PREFETCH_MOBILE_CONCURRENCY
      : VARIANT_PREFETCH_DESKTOP_CONCURRENCY;
    const workerCount = Math.min(maxConcurrency, jobs.length);

    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    if (failed > 0) {
      console.warn(`[Aururio Prefetch] FINISHED: ${completed} ready / ${failed} failed`);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('pointerover', this.#aururioColorIntentHandler);
    this.removeEventListener('pointerdown', this.#aururioColorIntentHandler);
    this.removeEventListener('focusin', this.#aururioColorIntentHandler);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    const isCombinedListingChange = Boolean(selectedOption.dataset.connectedProductUrl);
    const shopifySection = !source && !isCombinedListingChange ? this.closest('[id^="shopify-section-"]') : null;
    const shopifySectionId = shopifySection?.id?.replace(/^shopify-section-/, '');

    if (shopifySectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${encodeURIComponent(shopifySectionId)}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  fetchUpdatedSection(requestUrl, morphElementSelector) {
    this.#abortController?.abort();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const label = VARIANT_PREFETCH_LABELS.get(requestUrl) || 'Variant';
    const startedAt = performance.now();

    const cachedResponse = VARIANT_RESPONSE_CACHE.get(requestUrl);
    const prefetchInFlight = VARIANT_PREFETCH_INFLIGHT.get(requestUrl);

    const fetchLiveResponse = () => {
      return fetch(requestUrl, {
        signal,
        credentials: 'same-origin',
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Variant request failed with status ${response.status}`);
          }
          return response.text();
        })
        .then((responseText) => {
          cacheVariantResponse(requestUrl, responseText);
          return responseText;
        });
    };

    let responsePromise;
    let responseSource;

    if (cachedResponse) {
      responseSource = 'CACHE';
      responsePromise = Promise.resolve(cachedResponse);
    } else if (prefetchInFlight) {
      responseSource = 'PREFETCH-IN-FLIGHT';
      responsePromise = prefetchInFlight.catch(() => {
        responseSource = 'NETWORK';
        return fetchLiveResponse();
      });
    } else {
      responseSource = 'NETWORK';
      responsePromise = fetchLiveResponse();
    }

    responsePromise
      .then((responseText) => {
        if (signal.aborted) return;

        const applyStartedAt = performance.now();
        const result = this.#applyVariantResponse(
          responseText,
          requestUrl,
          morphElementSelector,
          signal
        );

        const applyDuration = Math.round(performance.now() - applyStartedAt);
        const totalDuration = Math.round(performance.now() - startedAt);

        if (applyDuration > 300) {
          console.warn(
            `[Aururio Variant] Slow apply: ${label} | source=${responseSource} | apply=${applyDuration}ms | total=${totalDuration}ms`
          );
        }

        return result;
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(`[Aururio Variant] FAILED: ${label}`, error);
      });
  }

  #applyVariantResponse(responseText, requestUrl, morphElementSelector, signal) {
    if (signal.aborted) return;

    const html = new DOMParser().parseFromString(responseText, 'text/html');
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

    const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;

    if (!textContent && requestUrl.includes('section_id=')) {
      const fallbackUrl = new URL(requestUrl, window.location.origin);
      fallbackUrl.searchParams.delete('section_id');
      const fallbackRequestUrl = `${fallbackUrl.pathname}${fallbackUrl.search}`;

      fetch(fallbackRequestUrl, { signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Variant fallback request failed with status ${response.status}`);
          return response.text();
        })
        .then((fallbackText) => {
          cacheVariantResponse(fallbackRequestUrl, fallbackText);
          this.#applyVariantResponse(fallbackText, fallbackRequestUrl, morphElementSelector, signal);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') console.error(error);
        });
      return;
    }

    if (!textContent) return;
    this.#pendingRequestUrl = undefined;

    let newProduct;

    if (morphElementSelector === 'main') {
      this.updateMain(html);
    } else if (morphElementSelector) {
      this.updateElement(html, morphElementSelector);
    } else {
      const { overflowList } = this.refs;
      const wasSwatchesExpanded =
        overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

      newProduct = this.updateVariantPicker(html);

      if (wasSwatchesExpanded) {
        const overflowListAfterMorph = overflowList;
        if (overflowListAfterMorph instanceof OverflowList) {
          overflowListAfterMorph.showAll();
        }
      }
    }

    if (this.selectedOptionId) {
      this.dispatchEvent(
        new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
          html,
          productId: this.dataset.productId ?? '',
          newProduct,
        })
      );
    }
  }

  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        return node.dataset.key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
